import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  where,
  getDoc,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import { useToast } from '../components/ui/Toast';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Input } from '../components/ui/FormField';
import { 
  Search, 
  Phone, 
  Calendar,
  User,
  Shield,
  Trash2,
  RefreshCw,
  Ban,
  Download,
  Plus,
  X
} from 'lucide-react';

const BlockedNumbers = () => {
  const [blockedNumbers, setBlockedNumbers] = useState([]);
  const [filteredNumbers, setFilteredNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, customer, driver
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const { toast } = useToast();
  const { user: currentAdmin, isMasterAdmin } = useAuth();
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [loadingOwner, setLoadingOwner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newReason, setNewReason] = useState('');
  const [submittingAdd, setSubmittingAdd] = useState(false);

  useEffect(() => {
    fetchBlockedNumbers();
  }, []);

  useEffect(() => {
    filterNumbers();
    setCurrentPage(1); // Reset to first page when filters change
  }, [blockedNumbers, searchTerm, filterType]);

  const fetchBlockedNumbers = async () => {
    try {
      setLoading(true);
      const blockedRef = collection(db, 'BlockedNumbers');
      const q = query(blockedRef, orderBy('blockedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const numbersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBlockedNumbers(numbersData);
    } catch (error) {
      console.error('Error fetching blocked numbers:', error);
      toast.error('Failed to fetch blocked numbers');
    } finally {
      setLoading(false);
    }
  };

  const filterNumbers = () => {
    let filtered = [...blockedNumbers];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.number?.toLowerCase().includes(search) ||
        item.owner?.name?.toLowerCase().includes(search) ||
        item.owner?.id?.toLowerCase().includes(search) ||
        item.blockedBy?.toLowerCase().includes(search)
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.owner?.type === filterType);
    }

    setFilteredNumbers(filtered);
  };

  const unblockNumber = async (blockedNumber) => {
    if (!isMasterAdmin) {
      toast.error('Only master admin can unblock numbers');
      return;
    }

    if (!window.confirm(`Are you sure you want to unblock ${blockedNumber.number}?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const blockedRef = doc(db, 'BlockedNumbers', blockedNumber.id);
      await deleteDoc(blockedRef);

      logAdminActivity({
        actor: currentAdmin,
        action: 'unblock_number',
        targetType: 'number',
        targetId: blockedNumber.id,
        targetLabel: blockedNumber.number,
      });

      await fetchBlockedNumbers();
      toast.success(`Phone number ${blockedNumber.number} has been unblocked`);
    } catch (error) {
      console.error('Error unblocking number:', error);
      toast.error(`Failed to unblock number: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const openAddModal = () => {
    setNewNumber('');
    setNewReason('');
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (submittingAdd) return;
    setShowAddModal(false);
  };

  const submitAddNumber = async () => {
    const normalized = newNumber.trim();
    if (!normalized) {
      toast.error('Phone number is required');
      return;
    }
    // Basic shape check — keep digits + leading + and spaces / dashes.
    if (!/^[+0-9\s\-()]{4,}$/.test(normalized)) {
      toast.error('Enter a valid phone number');
      return;
    }
    try {
      setSubmittingAdd(true);
      const blockedRef = collection(db, 'BlockedNumbers');
      // Refuse duplicates.
      const dup = await getDocs(query(blockedRef, where('number', '==', normalized)));
      if (!dup.empty) {
        toast.error('This number is already blocked');
        setSubmittingAdd(false);
        return;
      }
      const newDocRef = await addDoc(blockedRef, {
        number: normalized,
        reason: newReason.trim() || 'admin_manual_block',
        blockedAt: serverTimestamp(),
        blockedBy: currentAdmin?.name || currentAdmin?.email || 'admin',
        blockedByUid: currentAdmin?.uid || null,
        source: 'admin_panel',
      });
      logAdminActivity({
        actor: currentAdmin,
        action: 'block_number',
        targetType: 'number',
        targetId: newDocRef.id,
        targetLabel: normalized,
      });
      toast.success(`${normalized} has been blocked`);
      setShowAddModal(false);
      await fetchBlockedNumbers();
    } catch (error) {
      console.error('Error blocking number:', error);
      toast.error(`Failed to block number: ${error.message}`);
    } finally {
      setSubmittingAdd(false);
    }
  };

  const viewDetails = (blockedNumber) => {
    setSelectedNumber(blockedNumber);
    setShowDetailsModal(true);
  };

  const viewOwner = async (ownerId) => {
    try {
      setLoadingOwner(true);
      const ownerRef = doc(db, 'users', ownerId);
      const ownerDoc = await getDoc(ownerRef);
      
      if (ownerDoc.exists()) {
        setSelectedOwner({
          id: ownerDoc.id,
          ...ownerDoc.data()
        });
        setShowOwnerModal(true);
      } else {
        toast.error('Owner not found');
      }
    } catch (error) {
      console.error('Error fetching owner:', error);
      toast.error(`Failed to fetch owner details: ${error.message}`);
    } finally {
      setLoadingOwner(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTypeBadge = (type) => {
    if (type === 'customer') {
      return <Badge variant="secondary">Customer</Badge>;
    } else if (type === 'driver') {
      return <Badge variant="warning">Driver</Badge>;
    }
    return <Badge variant="muted">{type}</Badge>;
  };

  const exportToCSV = () => {
    if (filteredNumbers.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Phone Number', 'Owner Name', 'Owner Type', 'Blocked By', 'Blocked At', 'Owner ID'];
    const rows = filteredNumbers.map(item => [
      item.number || '',
      item.owner?.name || '',
      item.owner?.type || '',
      item.blockedBy || '',
      formatDate(item.blockedAt),
      item.owner?.id || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `blocked_numbers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Blocked numbers exported successfully');
  };

  // Pagination
  const totalPages = Math.ceil(filteredNumbers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNumbers = filteredNumbers.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading blocked numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-arabic">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blocked Numbers</h1>
          <p className="text-gray-600 mt-1">Manage blocked phone numbers</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchBlockedNumbers}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={openAddModal}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Number
          </Button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={filteredNumbers.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Blocked</p>
                <p className="text-2xl font-bold text-gray-900">{blockedNumbers.length}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <Ban className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Customers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blockedNumbers.filter(n => n.owner?.type === 'customer').length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <User className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Drivers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blockedNumbers.filter(n => n.owner?.type === 'driver').length}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <User className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search by phone, name, or blocked by..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'primary' : 'outline'}
                onClick={() => setFilterType('all')}
                size="sm"
              >
                All ({blockedNumbers.length})
              </Button>
              <Button
                variant={filterType === 'customer' ? 'primary' : 'outline'}
                onClick={() => setFilterType('customer')}
                size="sm"
              >
                Customers ({blockedNumbers.filter(n => n.owner?.type === 'customer').length})
              </Button>
              <Button
                variant={filterType === 'driver' ? 'primary' : 'outline'}
                onClick={() => setFilterType('driver')}
                size="sm"
              >
                Drivers ({blockedNumbers.filter(n => n.owner?.type === 'driver').length})
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Blocked Numbers Table */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">
            Blocked Numbers ({filteredNumbers.length})
          </h2>
          {filteredNumbers.length > itemsPerPage && (
            <p className="text-sm text-gray-600 mt-1">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredNumbers.length)} of {filteredNumbers.length}
            </p>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {filteredNumbers.length === 0 ? (
            <div className="text-center py-12">
              <Ban className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No blocked numbers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Blocked By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Blocked At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedNumbers.map((blockedNumber) => (
                    <tr key={blockedNumber.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {blockedNumber.number}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => viewOwner(blockedNumber.owner?.id)}
                          disabled={loadingOwner}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline disabled:opacity-50"
                        >
                          {blockedNumber.owner?.name || 'N/A'}
                        </button>
                        <div className="text-xs text-gray-500 cursor-pointer hover:text-gray-700"
                          onClick={() => viewOwner(blockedNumber.owner?.id)}
                        >
                          ID: {blockedNumber.owner?.id?.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(blockedNumber.owner?.type)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Shield className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {blockedNumber.blockedBy === 'admin_master' ? (
                              <span className="font-semibold text-purple-600">Admin Master</span>
                            ) : (
                              blockedNumber.blockedBy
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          {formatDate(blockedNumber.blockedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDetails(blockedNumber)}
                          >
                            Details
                          </Button>
                          {isMasterAdmin && (
                            <Button
                              variant="error"
                              size="sm"
                              onClick={() => unblockNumber(blockedNumber)}
                              disabled={loadingAction}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
        
        {/* Pagination */}
        {filteredNumbers.length > itemsPerPage && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className="min-w-[2.5rem]"
                      >
                        {page}
                      </Button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="px-2 py-1 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Details Modal */}
      {showDetailsModal && selectedNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-arabic">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Blocked Number Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Phone Number */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Phone Number</h4>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-lg font-semibold text-gray-900">{selectedNumber.number}</span>
                </div>
              </div>

              {/* Owner Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Owner Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedNumber.owner?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    {getTypeBadge(selectedNumber.owner?.type)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">User ID:</span>
                    <span className="text-sm font-mono text-gray-900">{selectedNumber.owner?.id}</span>
                  </div>
                </div>
              </div>

              {/* Block Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Block Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Blocked By:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedNumber.blockedBy === 'admin_master' ? (
                        <span className="font-semibold text-purple-600">Admin Master</span>
                      ) : (
                        selectedNumber.blockedBy
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Admin Type:</span>
                    <Badge variant={selectedNumber.blockedByType === 'admin_master' ? 'primary' : 'default'}>
                      {selectedNumber.blockedByType}
                    </Badge>
                  </div>
                  {selectedNumber.blockedByType !== 'admin_master' && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Admin UID:</span>
                      <span className="text-sm font-mono text-gray-700">{selectedNumber.blockedByUid}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Blocked At:</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedNumber.blockedAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Updated At:</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedNumber.updatedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Reason / note — captured at block time. Renders full-width
                  since notes are often multi-line and don't fit the
                  key/value strip above. Falls back to a subtle "no note"
                  hint when empty so it's obvious the admin didn't add one. */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Reason / Note</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  {selectedNumber.reason && selectedNumber.reason !== 'admin_manual_block' ? (
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {selectedNumber.reason}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No note provided.</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              {isMasterAdmin && (
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    variant="error"
                    className="w-full"
                    onClick={() => {
                      setShowDetailsModal(false);
                      unblockNumber(selectedNumber);
                    }}
                    disabled={loadingAction}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Unblock This Number
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Owner Details Modal */}
      {showOwnerModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-arabic">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">User Information</h3>
              <button
                onClick={() => setShowOwnerModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Basic Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedOwner.name || selectedOwner.full_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="text-sm font-mono text-gray-900">{selectedOwner.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    {getTypeBadge(selectedOwner.type)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <Badge variant={selectedOwner.status === 'active' ? 'success' : 'error'}>
                      {selectedOwner.status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">User ID:</span>
                    <span className="text-sm font-mono text-gray-700">{selectedOwner.id}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {(selectedOwner.phoneNumber || selectedOwner.phone_number) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Contact Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-sm font-semibold text-gray-900">
                        {selectedOwner.phoneNumber || selectedOwner.phone_number}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Address Information (for customers/drivers) */}
              {(selectedOwner.address || selectedOwner.city || selectedOwner.area) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Address Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {selectedOwner.address && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Address:</span>
                        <span className="text-sm text-gray-900">{selectedOwner.address}</span>
                      </div>
                    )}
                    {selectedOwner.city && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">City:</span>
                        <span className="text-sm text-gray-900">{selectedOwner.city}</span>
                      </div>
                    )}
                    {selectedOwner.area && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Area:</span>
                        <span className="text-sm text-gray-900">{selectedOwner.area}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Account Dates */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Account Dates</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Created At:</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedOwner.createdAt || selectedOwner.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Updated At:</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedOwner.updatedAt || selectedOwner.updated_at)}</span>
                  </div>
                  {selectedOwner.bannedAt && (
                    <div className="flex justify-between text-red-600">
                      <span className="text-sm text-red-500">Banned At:</span>
                      <span className="text-sm font-medium">{formatDate(selectedOwner.bannedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Stats (for drivers) */}
              {selectedOwner.type === 'driver' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Driver Stats</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Rating:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOwner.rating || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Orders Delivered:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOwner.totalOrdersDelivered || selectedOwner.total_orders_delivered || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Wallet Balance:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOwner.walletBalance || selectedOwner.wallet_balance || '0 JOD'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Stats (for customers) */}
              {selectedOwner.type === 'customer' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Customer Stats</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Rating:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOwner.rating || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Orders:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOwner.totalOrders || selectedOwner.total_orders || '0'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeAddModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-red-600">
                <Ban className="w-5 h-5" /> Block a Number
              </h3>
              <button
                onClick={closeAddModal}
                disabled={submittingAdd}
                className="p-1 rounded hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone number *
                </label>
                <Input
                  type="tel"
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  disabled={submittingAdd}
                  placeholder="+962XXXXXXXXX"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the full international number (e.g. <code>+962799123456</code>).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  disabled={submittingAdd}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                  placeholder="e.g. Fraudulent activity, repeated abuse"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeAddModal} disabled={submittingAdd}>
                Cancel
              </Button>
              <button
                onClick={submitAddNumber}
                disabled={submittingAdd}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                {submittingAdd ? 'Blocking...' : 'Block Number'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedNumbers;